import { getCsrfToken, type ShotFrameSource } from "@/lib/workspace-api"
import { handleJson } from "@/lib/json-request"

export type ShotReferenceInputKind = "storyboard" | "video" | "audio" | "voice"

export type ShotReferenceInputRow = {
  id: number
  kind: string
  position: number
  label: string
  url: string | null
  /** Chip / grid (~200). Image only; video/audio omit so `<img>` never gets a media file. */
  thumb_url?: string | null
  /** Original for lightbox / sketch / fetch (proxy). Same as `url` for images. */
  download_url?: string | null
  signed_id: string | null
  /** id блоба: по нему пикер Библиотеки гасит уже прикреплённую загрузку проекта. */
  blob_id?: number | null
  media_type: "image" | "video" | "audio" | "voice"
  created_at?: string | null
  /** Seconds from blob metadata when this ref is a video/audio file. */
  duration_seconds?: number | null
  /** Только kind=voice: герой, чей активный голос используется как референс */
  character_id?: number | null
  character_name?: string
  character_thumb_url?: string | null
  voice_ready?: boolean
}

export async function listShotReferenceInputs(
  projectId: number,
  shotId: number,
  kind: ShotReferenceInputKind,
): Promise<ShotReferenceInputRow[]> {
  const qs = new URLSearchParams({ kind })
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/reference_inputs?${qs}`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  })
  const data = await handleJson<{ inputs: ShotReferenceInputRow[] }>(res)
  return data.inputs
}

export async function addShotReferenceInput(
  projectId: number,
  shotId: number,
  file: File,
  kind: ShotReferenceInputKind,
): Promise<ShotReferenceInputRow> {
  const form = new FormData()
  form.append("file", file)
  form.append("kind", kind)
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/reference_inputs`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: form,
  })
  const data = await handleJson<{ ok: boolean; input: ShotReferenceInputRow }>(res)
  return data.input
}

/** Референс «из библиотеки»: вместо файла — source из пикера кадров, blob резолвится на сервере в границах проекта. */
export async function addShotReferenceInputFromSource(
  projectId: number,
  shotId: number,
  kind: Extract<ShotReferenceInputKind, "storyboard" | "video" | "audio">,
  source: ShotFrameSource,
): Promise<ShotReferenceInputRow> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/reference_inputs`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ kind, source }),
  })
  const data = await handleJson<{ ok: boolean; input: ShotReferenceInputRow }>(res)
  return data.input
}

export async function addShotVoiceReference(
  projectId: number,
  shotId: number,
  characterId: number,
): Promise<ShotReferenceInputRow> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/reference_inputs`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ kind: "voice", character_id: characterId }),
  })
  const data = await handleJson<{ ok: boolean; input: ShotReferenceInputRow }>(res)
  return data.input
}

export async function removeShotReferenceInput(projectId: number, shotId: number, inputId: number): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/reference_inputs/${inputId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
  })
  await handleJson<{ ok: boolean }>(res)
}
